import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  TrendingUp, DollarSign, Users, ClipboardList, Loader2, ArrowUpRight,
  AlertCircle, Package, Download, Sunrise, X, Clock, PackageCheck, Sparkles,
  Timer, Wrench,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtFull = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Card = ({ children, className = "", ...props }) => (
  <div
    className={`rounded-xl bg-white/[0.04] border border-white/[0.07] backdrop-blur-sm shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] ${className}`}
    {...props}
  >
    {children}
  </div>
);

const KpiCard = ({ label, value, sub, icon: Icon, accent }) => (
  <Card className="p-5 sm:p-6">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">{label}</p>
        <p className={`font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold mt-2 ${accent ? "text-spark-yellow" : "text-white"}`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
      </div>
      {Icon && (
        <div className={`size-10 rounded-md flex items-center justify-center ${accent ? "bg-spark-yellow/15 text-spark-yellow" : "bg-spark-teal/20 text-spark-teal"}`}>
          <Icon className="size-5" />
        </div>
      )}
    </div>
  </Card>
);

const RevenueSpark = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const W = 320;
  const H = 60;
  const stepX = W / Math.max(1, data.length - 1);
  const points = data.map((d, i) => `${(i * stepX).toFixed(1)},${(H - (d.revenue / max) * H).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-16">
      <defs>
        <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#FFD93D" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FFD93D" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${points} ${W},${H}`} fill="url(#sparkfill)" />
      <polyline points={points} fill="none" stroke="#FFD93D" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const STATUS_COLOURS = {
  "New": "#FFD93D",
  "Awaiting Drop-off": "#0EA5E9",
  "Received": "#8B5CF6",
  "Awaiting Quote Approval": "#EC4899",
  "In Progress": "#178B8E",
  "Long-Term Project": "#6366F1",
  "Waiting on Parts": "#F97316",
  "Ready for Pickup": "#10B981",
  "Completed": "#94a3b8",
  "Cancelled": "#475569",
};

const STATUS_ORDER = ["New", "Awaiting Drop-off", "Received", "Awaiting Quote Approval", "In Progress", "Long-Term Project", "Waiting on Parts", "Ready for Pickup", "Completed"];

const PipelineBar = ({ pipeline }) => {
  const total = STATUS_ORDER.reduce((s, st) => s + (pipeline[st] || 0), 0);
  if (total === 0) return <p className="text-sm text-slate-400">No jobs in workshop yet.</p>;
  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
        {STATUS_ORDER.map((st) => {
          const c = pipeline[st] || 0;
          if (!c) return null;
          return (
            <div
              key={st}
              style={{ width: `${(c / total) * 100}%`, background: STATUS_COLOURS[st] }}
              title={`${st}: ${c}`}
            />
          );
        })}
      </div>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
        {STATUS_ORDER.map((st) => (
          <li key={st} className="flex items-center gap-2 text-xs">
            <span className="size-2 rounded-full" style={{ background: STATUS_COLOURS[st] }} />
            <span className="text-slate-300 flex-1">{st}</span>
            <span className="text-white font-bold">{pipeline[st] || 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TopServices = ({ services }) => {
  if (!services?.length) return <p className="text-sm text-slate-400">No quoted services in the last year yet.</p>;
  const max = Math.max(...services.map((s) => s.count));
  return (
    <ul className="space-y-2.5">
      {services.map((s) => (
        <li key={s.sku + s.name} className="text-sm min-w-0">
          <div className="flex items-center justify-between gap-3 mb-1 min-w-0">
            <span className="text-white truncate flex-1 min-w-0">{s.name}</span>
            <span className="text-spark-yellow font-display font-bold tabular-nums shrink-0">{s.count}×</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-spark-teal to-spark-yellow" style={{ width: `${(s.count / max) * 100}%` }} />
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5 truncate">{fmtFull(s.revenue)} total</p>
        </li>
      ))}
    </ul>
  );
};

const AdminInsights = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorDetail, setErrorDetail] = useState(null);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const openBriefing = async () => {
    setBriefingOpen(true);
    setBriefingLoading(true);
    try {
      const res = await api.get("/admin/start-my-day");
      setBriefing(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load today's briefing");
    } finally {
      setBriefingLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setErrorDetail(null);
    try {
      const res = await api.get("/admin/insights");
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
        return;
      }
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      setErrorDetail(detail);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("spark_admin_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `spark-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <AdminLayout
      eyebrow="Workshop overview"
      title="Dashboard"
      actions={
        <>
          <button
            onClick={openBriefing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-300 transition-colors shadow-[0_1px_0_rgba(0,0,0,0.15)_inset]"
            data-testid="start-my-day-btn"
          >
            <Sunrise className="size-3.5" /> Start my day
          </button>
          <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-white/5 border border-white/10 text-xs uppercase tracking-wider font-semibold text-slate-200 hover:bg-white/10 transition-colors" data-testid="export-csv">
            <Download className="size-3.5" /> Export jobs · CSV
          </button>
        </>
      }
    >
      {loading ? (
        <div className="text-center py-20 text-slate-400" data-testid="dashboard-loading">
          <Loader2 className="size-6 animate-spin inline" />
        </div>
      ) : !data ? (
        <div className="text-center py-16 px-4" data-testid="dashboard-error">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-red-500/15 text-red-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h3 className="font-display uppercase tracking-tight text-xl font-bold text-white mb-2">Couldn't load the dashboard</h3>
          <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">Something went wrong fetching the workshop insights. Pull-to-refresh, or tap retry below. If it keeps failing, hit "Deploy" in chat to roll out the latest backend.</p>
          {errorDetail && <p className="text-xs text-slate-500 font-mono mb-4 break-all" data-testid="dashboard-error-detail">{String(errorDetail).slice(0, 240)}</p>}
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-400 transition" data-testid="dashboard-retry">
            <Loader2 className="size-3.5" /> Retry
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard
              label="Revenue · this month"
              value={fmt(data.kpis.revenue_month)}
              sub={`${fmt(data.kpis.revenue_ytd)} YTD`}
              icon={DollarSign}
              accent
            />
            <KpiCard
              label="Margin · YTD"
              value={fmt(data.kpis.margin_ytd)}
              sub={`${data.kpis.margin_pct}% margin`}
              icon={TrendingUp}
            />
            <KpiCard
              label="Jobs in workshop"
              value={data.kpis.jobs_open}
              sub={`${data.kpis.jobs_completed_month} completed this month`}
              icon={ClipboardList}
            />
            <KpiCard
              label="Customers"
              value={data.kpis.customers_total}
              sub={`${data.kpis.repeat_pct}% repeat`}
              icon={Users}
            />
          </div>

          {/* Revenue chart + pipeline */}
          <div className="grid lg:grid-cols-3 gap-4 min-w-0">
            <Card className="lg:col-span-2 p-5 sm:p-6 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Revenue · last 12 months</p>
                  <p className="font-display uppercase tracking-tight text-2xl font-bold text-white mt-1">{fmt(data.kpis.revenue_ytd)} <span className="text-spark-yellow text-sm">YTD</span></p>
                </div>
              </div>
              <RevenueSpark data={data.monthly_revenue} />
              <ul className="grid grid-cols-12 mt-2 text-[10px] text-slate-500 uppercase tracking-wider">
                {data.monthly_revenue.map((m) => (
                  <li key={m.key} className="text-center">{m.label}</li>
                ))}
              </ul>
            </Card>
            <Card className="p-5 sm:p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-4">Workshop pipeline</p>
              <PipelineBar pipeline={data.pipeline} />
            </Card>
          </div>

          {/* Top services + brands + recent activity */}
          <div className="grid lg:grid-cols-3 gap-4 min-w-0">
            <Card className="p-5 sm:p-6 min-w-0">
              <div className="flex items-center justify-between mb-4 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold truncate">Top services · 12 mo</p>
                <Link to="/admin/services" className="text-[10px] uppercase tracking-wider text-spark-yellow hover:text-white inline-flex items-center gap-1 shrink-0">
                  Catalogue <ArrowUpRight className="size-3" />
                </Link>
              </div>
              <TopServices services={data.top_services} />
            </Card>

            <Card className="p-5 sm:p-6 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-4">Top scooter brands</p>
              {!data.top_brands?.length ? (
                <p className="text-sm text-slate-400">No data yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {data.top_brands.map((b) => (
                    <li key={b.brand} className="flex items-center justify-between gap-3 text-sm min-w-0">
                      <span className="text-white truncate">{b.brand}</span>
                      <span className="text-spark-yellow font-display font-bold tabular-nums shrink-0">{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5 sm:p-6 min-w-0">
              <div className="flex items-center justify-between mb-4 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold truncate">Recent activity</p>
                <Link to="/admin/jobs" className="text-[10px] uppercase tracking-wider text-spark-yellow hover:text-white inline-flex items-center gap-1 shrink-0">
                  All jobs <ArrowUpRight className="size-3" />
                </Link>
              </div>
              {data.recent_jobs.length === 0 && data.recent_orders.length === 0 ? (
                <p className="text-sm text-slate-400">Nothing yet — bookings and orders will appear here.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.recent_jobs.map((j) => (
                    <li key={j.id} className="flex items-center gap-2 group min-w-0">
                      <span className="size-1.5 rounded-full bg-spark-teal shrink-0" />
                      <Link to={`/admin/jobs/${j.id}`} className="flex-1 min-w-0 truncate group-hover:text-white text-slate-300">
                        <span className="font-display uppercase tracking-wider text-xs text-spark-yellow mr-2">{j.ref_number}</span>
                        {j.full_name} · {j.scooter_brand}
                      </Link>
                      <span className="text-[10px] text-slate-500 shrink-0">{new Date(j.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}</span>
                    </li>
                  ))}
                  {data.recent_orders.map((o) => (
                    <li key={o.id} className="flex items-center gap-2 group min-w-0">
                      <Package className="size-3 text-spark-yellow shrink-0" />
                      <Link to={`/admin/shop`} className="flex-1 min-w-0 truncate group-hover:text-white text-slate-300">
                        <span className="font-display uppercase tracking-wider text-xs text-spark-yellow mr-2">{o.order_number}</span>
                        {o.customer_name} · {fmtFull(o.total)}
                      </Link>
                      <span className="text-[10px] text-slate-500 shrink-0">{new Date(o.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}
      {briefingOpen && (
        <StartMyDayModal
          briefing={briefing}
          loading={briefingLoading}
          onClose={() => setBriefingOpen(false)}
          onRefresh={openBriefing}
        />
      )}
    </AdminLayout>
  );
};

const STATUS_PILL = {
  "New": "bg-spark-yellow/20 text-spark-yellow border-spark-yellow/40",
  "Awaiting Drop-off": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "In Progress": "bg-spark-teal/25 text-spark-teal border-spark-teal/40",
  "Long-Term Project": "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  "Waiting on Parts": "bg-orange-500/20 text-orange-300 border-orange-500/40",
  "Ready for Pickup": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

const StatCell = ({ label, value, tone = "default", icon: Icon }) => {
  const toneClasses =
    tone === "warn" ? "border-orange-500/30 bg-orange-500/5"
    : tone === "success" ? "border-emerald-500/30 bg-emerald-500/5"
    : tone === "indigo" ? "border-indigo-500/30 bg-indigo-500/5"
    : "border-white/10 bg-white/[0.04]";
  return (
    <div className={`rounded-md border ${toneClasses} px-4 py-3 flex items-center gap-3`}>
      {Icon && <Icon className="size-4 text-slate-300 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">{label}</p>
        <p className="font-display text-2xl font-bold text-white leading-tight mt-0.5">{value}</p>
      </div>
    </div>
  );
};

const JobRow = ({ job, tone = "default", testidPrefix = "briefing-job" }) => {
  const pill = STATUS_PILL[job.status] || "bg-white/10 text-slate-300 border-white/20";
  const activityTone = job.days_since_update >= 14 ? "text-red-300" : job.days_since_update >= 7 ? "text-orange-300" : "text-slate-400";
  return (
    <Link
      to={`/admin/jobs/${job.id}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.06] transition group"
      data-testid={`${testidPrefix}-${job.ref_number}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display uppercase tracking-wider text-xs text-spark-yellow font-bold">{job.ref_number}</span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider font-bold ${pill}`}>
            {job.status}
          </span>
        </div>
        <p className="text-sm text-white truncate mt-0.5">{job.full_name} · <span className="text-slate-400">{job.scooter_brand} {job.scooter_model}</span></p>
      </div>
      <div className="text-right shrink-0">
        {tone === "age" ? (
          <p className="text-xs text-slate-300 whitespace-nowrap">
            <span className="font-display text-base font-bold text-white">{job.days_old}</span> day{job.days_old === 1 ? "" : "s"} old
          </p>
        ) : tone === "stale" ? (
          <p className={`text-xs whitespace-nowrap ${activityTone}`}>
            <span className="font-display text-base font-bold text-white">{job.days_since_update}</span> day{job.days_since_update === 1 ? "" : "s"} quiet
          </p>
        ) : tone === "ready" ? (
          <p className="text-xs text-emerald-300 whitespace-nowrap">
            Ready · <span className="font-display text-base font-bold text-white">{job.days_since_update}</span>d
          </p>
        ) : null}
      </div>
      <ArrowUpRight className="size-3.5 text-slate-500 group-hover:text-white shrink-0" />
    </Link>
  );
};

const Section = ({ title, count, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-baseline justify-between mb-2">
      <h4 className="font-display uppercase tracking-tight text-sm font-bold text-white">{title}</h4>
      {count !== undefined && <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{count} total</span>}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const StartMyDayModal = ({ briefing, loading, onClose, onRefresh }) => {
  const b = briefing;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" role="dialog" aria-modal="true" data-testid="start-my-day-modal">
      <div className="w-full max-w-3xl my-8 rounded-xl border border-white/10 bg-spark-teal-night shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-5 bg-gradient-to-br from-spark-teal-deep via-spark-teal-deep to-indigo-950 border-b border-white/10">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(600px circle at top right, rgba(255,217,61,0.25), transparent 60%)" }} />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="label-mono text-spark-yellow flex items-center gap-1.5">
                <Sunrise className="size-3.5" /> Start my day
              </p>
              <h2 className="font-display uppercase tracking-tight text-xl sm:text-2xl font-bold text-white mt-1">
                {b?.today || "Today's briefing"}
              </h2>
              <p className="text-xs text-slate-300 mt-1 max-w-md">
                A quick lay of the land — no orders, no priorities. Just the state of the bench so you can plan your day.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-white p-1" data-testid="briefing-close" title="Close">
              <X className="size-5" />
            </button>
          </div>
        </div>

        {loading || !b ? (
          <div className="py-20 text-center text-slate-400" data-testid="briefing-loading">
            <Loader2 className="size-6 animate-spin inline" />
            <p className="text-xs mt-2 uppercase tracking-wider">Reading the workshop…</p>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {/* Counts grid */}
            <div>
              <p className="label-mono text-spark-yellow mb-3">The bench right now</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="briefing-counts">
                <StatCell label="Active jobs" value={b.counts.active} icon={ClipboardList} />
                <StatCell label="In progress" value={b.counts.in_progress} icon={Wrench} />
                <StatCell label="Waiting on parts" value={b.counts.waiting_on_parts} tone="warn" icon={Package} />
                <StatCell label="Long-term projects" value={b.counts.long_term_project} tone="indigo" icon={Timer} />
                <StatCell label="Ready for collection" value={b.counts.ready_for_pickup} tone="success" icon={PackageCheck} />
                <StatCell label="New / intake" value={b.counts.new + b.counts.awaiting_dropoff} icon={AlertCircle} />
              </div>
            </div>

            {/* Suggestions — subtle observations, never orders */}
            {b.suggestions && b.suggestions.length > 0 && (
              <div className="rounded-md border border-spark-yellow/25 bg-spark-yellow/[0.06] p-4" data-testid="briefing-suggestions">
                <p className="label-mono text-spark-yellow flex items-center gap-1.5 mb-2">
                  <Sparkles className="size-3" /> If you&apos;re deciding where to start
                </p>
                <ul className="space-y-1.5 text-sm text-slate-100">
                  {b.suggestions.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-spark-yellow mt-1.5 shrink-0" aria-hidden>•</span>
                      <span className="flex-1">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Ready for collection */}
            {b.ready_for_pickup?.length > 0 && (
              <Section title="Ready to be collected" count={b.ready_for_pickup.length} testid="briefing-ready">
                {b.ready_for_pickup.map((j) => <JobRow key={j.id} job={j} tone="ready" testidPrefix="briefing-ready-job" />)}
              </Section>
            )}

            {/* Oldest active jobs */}
            {b.oldest_active?.length > 0 && (
              <Section title="Oldest jobs on the bench" testid="briefing-oldest">
                {b.oldest_active.map((j) => <JobRow key={j.id} job={j} tone="age" testidPrefix="briefing-oldest-job" />)}
              </Section>
            )}

            {/* Stale — no update in a week */}
            {b.stale_active?.length > 0 ? (
              <Section title="Quiet for a week or more" count={b.stale_active.length} testid="briefing-stale">
                <p className="text-xs text-slate-400 mb-1.5 -mt-1">No customer-facing update posted in 7+ days. A quick photo or one-liner keeps the tracker fresh.</p>
                {b.stale_active.map((j) => <JobRow key={j.id} job={j} tone="stale" testidPrefix="briefing-stale-job" />)}
              </Section>
            ) : b.counts.active > 0 && (
              <div className="rounded-md border border-emerald-500/25 bg-emerald-500/[0.05] p-3 text-sm text-emerald-200" data-testid="briefing-all-fresh">
                <Sparkles className="inline size-3.5 mr-1" />
                Every active job has been touched in the last week — nice work keeping customers in the loop.
              </div>
            )}

            <p className="text-[10px] text-slate-500 text-center pt-2 flex items-center justify-center gap-2">
              <Clock className="size-3" />
              Briefing generated {new Date(b.generated_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}
              <button onClick={onRefresh} className="text-spark-yellow hover:text-yellow-300 underline underline-offset-2" data-testid="briefing-refresh">refresh</button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInsights;
